/**
 * Utility functions for country detection
 */

/**
 * Get user's country from IP address
 * Uses ipapi.co free tier API with aggressive caching to avoid rate limits
 * 
 * @returns {Promise<{countryCode: string, countryName: string}>} Country information
 */
export const getUserCountryFromIP = async () => {
  try {
    // Default fallback
    const defaultCountry = { countryCode: 'US', countryName: 'United States' };
    
    // Check if we have cached country data
    const cachedCountry = localStorage.getItem('userCountry');
    const cacheTimestamp = localStorage.getItem('userCountryTimestamp');
    
    // If we have cached data and it's less than 7 days old, use it
    // Using a longer cache time to reduce API calls
    if (cachedCountry && cacheTimestamp) {
      const cacheAge = Date.now() - parseInt(cacheTimestamp, 10);
      // 7 days in milliseconds
      if (cacheAge < 7 * 24 * 60 * 60 * 1000) {
        return JSON.parse(cachedCountry);
      }
    }
    
    // Check for API call cooldown to prevent excessive API calls
    const lastAPICall = localStorage.getItem('lastIPApiCall');
    if (lastAPICall) {
      const timeSinceLastCall = Date.now() - parseInt(lastAPICall, 10);
      // If we've called the API in the last 10 minutes, use browser locale instead
      if (timeSinceLastCall < 10 * 60 * 1000) {
        return getBrowserLocaleCountry();
      }
    }
    
    // Set last API call timestamp
    localStorage.setItem('lastIPApiCall', Date.now().toString());
    
    // Try to get country from IP
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
    
    try {
      const response = await fetch('https://ipapi.co/json/', { 
        signal: controller.signal 
      });
      clearTimeout(timeoutId);
      
      // If response fails, use browser locale or fallback
      if (!response.ok) {
        const fallback = getBrowserLocaleCountry();
        cacheCountryData(fallback);
        return fallback;
      }
      
      const data = await response.json();
      
      // Return country code and name
      const result = {
        countryCode: data.country_code || 'US',
        countryName: data.country_name || 'United States'
      };
      
      // Cache the result
      cacheCountryData(result);
      
      return result;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      console.warn('IP API fetch failed:', fetchError);
      const fallback = getBrowserLocaleCountry();
      cacheCountryData(fallback);
      return fallback;
    }
  } catch (error) {
    console.warn('Failed to detect country from IP:', error);
    
    // Use browser locale as fallback
    return getBrowserLocaleCountry();
  }
};

/**
 * Get country from browser locale
 * @returns {Object} Country code and name
 */
const getBrowserLocaleCountry = () => {
  const countryCode = getCountryFromBrowserLocale();
  return { 
    countryCode, 
    countryName: '' // We don't have the name from the locale
  };
};

/**
 * Cache country data to localStorage
 * @param {Object} countryData - The country data to cache
 */
const cacheCountryData = (countryData) => {
  localStorage.setItem('userCountry', JSON.stringify(countryData));
  localStorage.setItem('userCountryTimestamp', Date.now().toString());
};

/**
 * Get browser language and region
 * This isn't as reliable as IP detection but can be used as a fallback
 * 
 * @returns {string} Country code based on browser language
 */
export const getCountryFromBrowserLocale = () => {
  try {
    const locale = navigator.language || navigator.userLanguage || 'en-US';
    // If locale includes a region code (e.g., en-US, zh-CN)
    if (locale.includes('-')) {
      return locale.split('-')[1];
    }
    // Default to US if no region code
    return 'US';
  } catch (error) {
    return 'US';
  }
}; 